import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, EyeIcon, UsersIcon, HeartHandshakeIcon, PuzzleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReviewsGallery } from '../components/ReviewsGallery';
import { EnrollmentOffer } from '../components/EnrollmentOffer';
import { FAQSection } from '../components/FAQSection';
export function Course2Page() {
  const modules = [{
    id: '01',
    title: 'Contact vizual și atenție împărtășită',
    icon: <EyeIcon size={48} className="text-brand-gold" data-id="element-597" />,
    bullets: ['Cum să obții și să menții contactul vizual cu copilul tău', 'Ce este atenția împărtășită și de ce este baza comunicării', 'Jocuri simple pentru a dezvolta atenția reciprocă']
  }, {
    id: '02',
    title: 'Imitarea - primul pas spre vorbire',
    icon: <UsersIcon size={48} className="text-brand-gold" data-id="element-598" />,
    bullets: ['Cum să îl înveți pe copil să imite gesturi, sunete și acțiuni', 'De ce copilul tău nu te imită și cum să schimbi asta', 'Cum să folosești oglindirea pentru a-i capta atenția']
  }, {
    id: '03',
    title: 'Interesul pentru oameni',
    icon: <HeartHandshakeIcon size={48} className="text-brand-gold" data-id="element-599" />,
    bullets: ['Cum să trezești interesul copilului pentru oameni, nu doar pentru obiecte', 'Jocuri sociale fără jucării (cucu-bau, gâdilat, alergat)', 'Cum să devii tu cea mai interesantă "jucărie" din cameră']
  }, {
    id: '04',
    title: 'Jocul funcțional și simbolic',
    icon: <PuzzleIcon size={48} className="text-brand-gold" data-id="element-600" />,
    bullets: ['Tipuri de joc potrivite fiecărei etape de dezvoltare', 'Cum să treci de la jocul repetitiv la jocul cu sens', 'Cum să transformi momentele de joc în momente de învățare']
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="pt-[64px] md:pt-[72px]" data-id="element-601">
      {/* SECȚIUNEA 1 — HERO OFERTĂ */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-602">
        <div className="container-custom" data-id="element-603">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-604">
            <div className="max-w-xl" data-id="element-605">
              <h1 className="font-playfair text-[48px] md:text-[64px] font-bold text-white mb-6 leading-[1.1]" data-id="element-606">
                Relaționare și Joc
              </h1>
              <p className="font-source text-[20px] md:text-[24px] text-brand-gold mb-10 italic" data-id="element-607">
                Construiește conexiunea cu copilul tău prin joc, imitare și
                atenție împărtășită, baza pe care se clădește tot restul.
              </p>
              <a href="#inscriere" className="btn-inverted text-[18px] px-8 py-4" data-id="element-608">
                Click aici pentru înscriere!
              </a>
            </div>
            <div className="relative rounded-[24px] overflow-hidden shadow-2xl border-4 border-white/10" data-id="element-609">
              <img src="https://assets.cdn.filesafe.space/3SQqBp51ZHIpTGzJ3Vu1/media/6a2bf3330b818c92b3663851.jpg" alt="Relaționare și Joc" className="w-full h-full object-cover aspect-[4/3]" data-id="element-610" />
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 2 — DESCRIERE */}
      <section className="bg-brand-beige section-padding" data-id="element-611">
        <div className="container-custom" data-id="element-612">
          <div className="max-w-3xl mx-auto" data-id="element-613">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy mb-8 text-center" data-id="element-614">
              Simți că nu reușești să te conectezi cu copilul tău?
            </h2>
            <div className="font-source text-[16px] md:text-[18px] text-brand-text space-y-6" data-id="element-615">
              <p data-id="element-616">
                Copilul tău nu te privește când îi vorbești. Nu răspunde când îl
                strigi pe nume. Se joacă singur, repetitiv, și pare mai
                interesat de obiecte decât de oameni. Uneori vine la tine doar
                când are nevoie de ceva, iar în rest e ca și cum nu ai fi acolo.
                Te întrebi dacă te iubește, dacă te vede, dacă simte ceva când
                ești lângă el.
              </p>
              <p data-id="element-617">
                Fără conexiune, niciun exercițiu de limbaj nu va funcționa.
                Copilul nu va imita, nu va răspunde, nu va comunica dacă nu e
                mai întâi interesat de tine ca persoană. Toate cursurile din
                lume nu ajută dacă baza lipsește.
              </p>
              <div className="bg-white p-6 rounded-[16px] border-l-4 border-brand-coral" data-id="element-618">
                <p className="font-medium text-brand-navy" data-id="element-619">
                  Cursul "R - Relaționare și Joc" te învață exact cum să
                  construiești această bază. Prin contact vizual, atenție
                  împărtășită, imitare și joc, copilul tău începe să te caute,
                  să te privească și să fie interesat de interacțiunea cu tine.
                  Aici se pune fundația pe care se clădește tot restul
                  dezvoltării.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 3 — CE VEI DESCOPERI */}
      <section className="bg-brand-ivory section-padding" data-id="element-620">
        <div className="container-custom" data-id="element-621">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-16" data-id="element-622">
            Ce vei descoperi concret în Cursul 2: Relaționare și Joc
          </h2>
          <div className="max-w-4xl mx-auto space-y-8" data-id="element-623">
            {modules.map(mod => <div key={mod.id} className="flex flex-col md:flex-row bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden p-8 gap-8 items-start" data-id="element-624">
                {/* Icon Side */}
                <div className="flex-shrink-0 w-24 h-24 rounded-full bg-brand-beige flex items-center justify-center" data-id="element-625">
                  {mod.icon}
                </div>

                {/* Content Side */}
                <div className="flex-grow" data-id="element-626">
                  <h3 className="font-playfair text-[24px] md:text-[28px] font-bold text-brand-navy mb-6 leading-[1.2]" data-id="element-627">
                    {mod.title}
                  </h3>
                  <ul className="space-y-4 flex flex-col" data-id="element-628">
                    {mod.bullets.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-629">
                        <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-1" size={20} data-id="element-630" />
                        <span className="font-source text-[15px] text-brand-text" data-id="element-631">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 4 — BENEFICII */}
      <section className="bg-brand-beige section-padding" data-id="element-632">
        <div className="container-custom" data-id="element-633">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-634">
            Ce se schimbă după ce parcurgi acest curs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-635">
            {[{
            title: 'Copilul începe să te caute prin casă',
            desc: 'Nu mai stă izolat. Te privește, vine la tine, e interesat de ce faci. Conexiunea se construiește pas cu pas.'
          }, {
            title: 'Începe să imite',
            desc: 'Imită gesturi, sunete, expresii faciale. Aceasta e poarta de intrare spre limbaj și comunicare.'
          }, {
            title: 'Jocul devine mai bogat și mai variat',
            desc: 'Nu mai face doar aceleași lucruri repetitiv. Începe să exploreze, să fie curios, să accepte jocuri noi.'
          }, {
            title: 'Știi cum să te joci cu copilul tău',
            desc: 'Nu mai spui "nu sunt genul care se joacă". Ai tehnici concrete, simple, pe care le aplici natural în viața de zi cu zi.'
          }].map((card, i) => <div key={i} className="bg-white p-6 rounded-[16px] shadow-sm border-t-4 border-brand-gold" data-id="element-636">
                <h3 className="font-montserrat font-bold text-[16px] text-brand-navy mb-3" data-id="element-637">
                  {card.title}
                </h3>
                <p className="font-source text-[14px] text-brand-text" data-id="element-638">
                  {card.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 5 — CINE SUNT EU */}
      <section className="bg-brand-ivory section-padding" data-id="element-639">
        <div className="container-custom" data-id="element-640">
          <div className="max-w-4xl mx-auto" data-id="element-641">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-10" data-id="element-642">
              Cine sunt eu și de ce am creat acest curs
            </h2>
            <div className="flex flex-col-reverse md:grid md:grid-cols-3 gap-8 items-stretch" data-id="element-643">
              <div className="md:col-span-2 font-source text-[16px] md:text-[18px] text-brand-text space-y-4 text-center md:text-left flex flex-col justify-center" data-id="element-644">
                <p data-id="element-645">
                  Sunt Cristina Geană, psiholog clinician cu peste 11 ani de
                  experiență cu copii mici. Am văzut de sute de ori cum se
                  schimbă totul în momentul în care copilul începe să se
                  conecteze cu mama lui. Totul pornește de acolo: limbajul,
                  comunicarea, comportamentul.
                </p>
                <p data-id="element-646">
                  Am creat acest curs pentru că știu că multe mame simt că "nu
                  se pot juca" sau că "nu funcționează nimic" cu copilul lor. Și
                  pentru că soluția nu e să forțezi, ci să construiești
                  conexiunea pas cu pas, prin joc.
                </p>
              </div>
              <div className="md:col-span-1" data-id="element-647">
                <img src="https://cdn.magicpatterns.com/uploads/wvUBHAy6VhqaRqNiVJPB6X/Cristina_poze_%285%29.jpg" alt="Cristina Geană" className="w-full h-full object-cover object-top rounded-[24px] shadow-card" data-id="element-648" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 6 — PENTRU CINE ESTE / NU ESTE */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-649">
        <div className="container-custom" data-id="element-650">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white text-center mb-12" data-id="element-651">
            Pentru cine este acest curs?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-652">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-[24px] border border-white/20" data-id="element-653">
              <h3 className="font-playfair text-[28px] font-bold text-brand-gold mb-6 !text-brand-gold" data-id="element-654">
                Pentru cine este
              </h3>
              <ul className="space-y-4" data-id="element-655">
                {['Mame de copii care par "în lumea lor" și nu răspund când sunt strigați', 'Mame de copii care se joacă repetitiv (aliniază jucării, învârt roți) și nu știu cum să se joace cu sens', 'Mame care simt că nu reușesc să se conecteze cu copilul lor'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-656">
                    <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-0.5" size={20} data-id="element-657" />
                    <span className="font-source text-[15px] text-white/90" data-id="element-658">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-white/5 p-8 rounded-[24px] border border-white/10" data-id="element-659">
              <h3 className="font-playfair text-[28px] font-bold text-white mb-6 !text-white" data-id="element-660">
                Pentru cine NU este
              </h3>
              <ul className="space-y-4" data-id="element-661">
                {['Mame care nu sunt dispuse să aloce timp zilnic pentru joc și interacțiune cu copilul', 'Mame care caută rezultate imediate fără efort constant', 'Mame ai căror copii au tulburări asociate complexe care necesită în primul rând intervenție medicală'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-662">
                    <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-663">
                      <span className="w-1.5 h-1.5 rounded-full bg-white/60" data-id="element-664"></span>
                    </div>
                    <span className="font-source text-[15px] text-white/70" data-id="element-665">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 7 — OFERTĂ */}
      <EnrollmentOffer title="Ce primești și care este investiția ta" includes={['Acces pe viață la Modulul 2 - Relaționare și Joc', 'Materiale video structurate pas cu pas', 'Tehnici practice de aplicat acasă', 'Informație clară, fără termeni medicali']} price="395 lei" note="Acest modul face parte din Colecția Completă Uite cum C.R.E.S.C., dar poate fi achiziționat și separat." data-id="element-666" />

      {/* SECȚIUNEA 8 — TESTIMONIALE */}
      <section className="bg-brand-ivory section-padding" data-id="element-667">
        <div className="container-custom" data-id="element-668">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-669">
            Ce spun mamele care deja au lucrat cu mine
          </h2>
          <ReviewsGallery data-id="element-670" />
        </div>
      </section>

      {/* SECȚIUNEA 9 — FAQ */}
      <FAQSection faqs={[{
      q: 'Când primesc acces la curs?',
      a: 'Imediat după confirmarea plății, vei primi pe email datele de acces la platforma unde se află cursul.'
    }, {
      q: 'Cât timp am acces la materiale?',
      a: 'Ai acces pe viață la acest modul. Îl poți revedea ori de câte ori ai nevoie, în ritmul tău.'
    }, {
      q: 'Pot cumpăra și restul modulelor mai târziu?',
      a: 'Da, poți achiziționa celelalte module separat sau poți opta pentru Colecția Completă C.R.E.S.C. pentru a beneficia de prețul special și de apelurile de grup.'
    }]} data-id="element-671" />

      {/* SECȚIUNEA 10 — ÎNSCRIERE */}
      <section id="inscriere" className="bg-brand-navy section-padding text-center text-white" data-id="element-672">
        <div className="container-custom" data-id="element-673">
          <div className="max-w-3xl mx-auto" data-id="element-674">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white mb-6" data-id="element-675">
              Înscriere Modulul 2 - Relaționare și Joc
            </h2>
            <p className="font-source text-[18px] text-white/80 mb-8" data-id="element-676">
              Completează formularul de mai jos pentru a finaliza înscrierea.
            </p>

            <div className="bg-white rounded-[24px] overflow-hidden min-h-[600px]" data-id="element-677">
              <iframe src="https://api.leadconnectorhq.com/widget/form/qywrBLc772X0pXiQHX9c" style={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              border: 'none'
            }} id="inline-qywrBLc772X0pXiQHX9c" data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-trigger-value="" data-activation-type="alwaysActivated" data-activation-value="" data-deactivation-type="neverDeactivate" data-deactivation-value="" data-form-name="Form Modul 2" data-height="600" data-layout-iframe-id="inline-qywrBLc772X0pXiQHX9c" data-form-id="qywrBLc772X0pXiQHX9c" title="Form Modul 2" data-id="element-678" />
            </div>
          </div>
        </div>
      </section>
    </motion.div>;
}